import React, { useEffect, useRef, useState } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaSearch,
  FaPlus,
  FaEllipsisH,
  FaCog,
} from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import Modal from "../Modals/Modal";
import ConfirmModal from "../Modals/ConfirmModal";
import GestionMasivaModal from "../GestionMasivaModal";
import Pagination from "../Pagination";
import ProgressiveNotification from "../ProgressiveNotification";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../context/UsersContext";
import { useAuth } from "../../context/AuthContext";

const normalizeText = (text) => {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
};

const UserList = ({ users, type }) => {
  const navigate = useNavigate();
  const { salones, fetchSalones } = useUsers();
  const [modalData, setModalData] = useState({
    isOpen: false,
    title: "",
    content: "",
  });
  const [gestionMasivaOpen, setGestionMasivaOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGrado, setSelectedGrado] = useState(""); // Filtro por grado
  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga
  const { deleteUser } = useUsers(); // Usamos la función del contexto
  const { user } = useAuth(); // Usuario autenticado
  const isRestricted = user.rol_id === 2 && type === "students"; // Verificar si el usuario es un profesor y está viendo estudiantes
  const actionMenuRef = useRef(null); // Referencia para detectar clics fuera del menú
  const filterMenuRef = useRef(null); // Referencia para detectar clics fuera del filtro
  const [currentPage, setCurrentPage] = useState(1);
  const usuariosPorPagina = 9; // Máximo 9 usuarios por página
  const [imageErrors, setImageErrors] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [resultadoProgresivo, setResultadoProgresivo] = useState(null);

  useEffect(() => {
    fetchSalones();
  }, [fetchSalones]);

  useEffect(() => {
    const filtered = users.filter((user) => {
      const matchesGrado =
        selectedGrado === "" ||
        salones.find((salon) => salon.nombre === user.salon_nombre)?.grado ===
          selectedGrado;

      return matchesGrado;
    });
    setFilteredUsers(filtered);
    setCurrentPage(1);
    // Inicializar estado de carga para nuevos usuarios
    const loadingState = {};
    filtered.forEach(user => {
      if (!imageErrors[user.user_id]) {
        loadingState[user.user_id] = true;
      }
    });
    setImageLoading(loadingState);
  }, [selectedGrado, users, salones]);

  // Lógica para cerrar menú al hacer clic fuera o al redimensionar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenu(null);
      }

      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setIsFilterMenuOpen(false);
      }
    };

    const handleResize = () => {
      setActionMenu(null);
      setIsFilterMenuOpen(false); // Cerrar el filtro si cambia el tamaño de la pantalla
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Lógica de paginación
  const indexOfLastUser = currentPage * usuariosPorPagina;
  const indexOfFirstUser = indexOfLastUser - usuariosPorPagina;
  const usuariosActuales = filteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );
  const totalPaginas = Math.ceil(filteredUsers.length / usuariosPorPagina);

  const handleShowModal = (title, content) => {
    setModalData({ isOpen: true, title, content });
  };

  const handleCloseModal = () => {
    setModalData({ isOpen: false, title: "", content: "" });
  };

  const toggleFilterMenu = () => {
    setIsFilterMenuOpen((prevState) => !prevState);
  };

  const handleSearch = (e) => {
    const searchValue = normalizeText(e.target.value);
    setFilteredUsers(
      users.filter((user) =>
        normalizeText(
          `${user.first_name} ${user.second_name} ${user.first_surname}`
        ).includes(searchValue)
      )
    );
  };

  const toggleActionMenu = (userId, event) => {
    if (actionMenu === userId) {
      setActionMenu(null);
    } else {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      setActionMenu(userId);
      setActionMenuPosition({
        top: buttonRect.bottom + window.scrollY,
        left: buttonRect.left + window.scrollX - 100,
      });
    }
  };

  const handleImageError = (userId) => {
    setImageErrors((prev) => ({
      ...prev,
      [userId]: true,
    }));
    setImageLoading((prev) => ({
      ...prev,
      [userId]: false,
    }));
  };

  const handleImageLoad = (userId) => {
    setImageLoading((prev) => ({
      ...prev,
      [userId]: false,
    }));
  };

  const handleCreateNew = () => {
    if (type === "students") {
      navigate("/estudiantes/nuevo");
    } else if (type === "teachers") {
      navigate("/profesores/nuevo");
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      if (selectedUserId) {
        await deleteUser(selectedUserId);
        setIsModalOpen(false);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (userId) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedUserId(null);
    setIsModalOpen(false);
  };

  return (
    <div className="container mx-auto">
      {/* Controles de búsqueda y acciones */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        {/* Barra de búsqueda */}
        <div className="flex items-center w-full md:w-1/4 bg-white border border-gray rounded-full px-4 py-2">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="Buscar..."
            className="w-full outline-none text-gray "
            onChange={handleSearch}
          />
        </div>

        {/* Botones de filtro y nuevo */}
        <div className="mt-4 md:mt-0 flex space-x-4">
          {/* Menú desplegable de filtro */}
          <div className="relative" ref={filterMenuRef}>
            <button
              onClick={toggleFilterMenu}
              className="flex items-center text-base px-4 py-3 border border-main-blue text-main-blue rounded-full hover:bg-main-blue hover:text-white"
            >
              Filtro
              <FiChevronDown className="ml-2" />
            </button>
            {isFilterMenuOpen && (
              <div className="absolute bg-white border border-gray rounded-lg mt-2 w-48 z-10">
                <ul>
                  <li
                    className="px-4 py-3 hover:bg-gray cursor-pointer"
                    onClick={() => {
                      setFilteredUsers(
                        [...users].sort((a, b) =>
                          a.first_name.localeCompare(b.first_name)
                        )
                      );
                      setIsFilterMenuOpen(false);
                    }}
                  >
                    Nombre (Ascendente)
                  </li>
                  {type !== "students" && (
                    <li
                      className="px-4 py-3 hover:bg-gray cursor-pointer"
                      onClick={() => {
                        setFilteredUsers(
                          users.filter(
                            (user) =>
                              user.salon_director &&
                              user.salon_director !== "Ninguno"
                          )
                        );
                        setIsFilterMenuOpen(false);
                      }}
                    >
                      Solo Directores
                    </li>
                  )}
                  {type === "students" && (
                    <>
                      {/* Filtro por grado */}
                      {[...new Set(salones.map((salon) => salon.grado))].map(
                        (grado) => (
                          <li
                            key={grado}
                            className="px-4 py-3 hover:bg-gray cursor-pointer"
                            onClick={() => {
                              setSelectedGrado(grado);
                              setIsFilterMenuOpen(false);
                            }}
                          >
                            {grado}
                          </li>
                        )
                      )}
                      <li
                        className="px-4 py-3 hover:bg-gray cursor-pointer"
                        onClick={() => {
                          setSelectedGrado("");
                          setIsFilterMenuOpen(false);
                        }}
                      >
                        Todos los Grados
                      </li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Botón de nuevo, visible solo si no está restringido */}
          {!isRestricted && (
            <button
              onClick={handleCreateNew}
              className="flex items-center px-4 py-3 text-base bg-main-blue text-white rounded-full hover:bg-blue-800"
            >
              <FaPlus className="mr-2" /> Nuevo
            </button>
          )}

          {/* Botón de gestión masiva (solo para administradores) */}
          {user.rol_id === 1 && (
            <button
              onClick={() => setGestionMasivaOpen(true)}
              className="bg-main-blue hover:bg-main-blue-dark text-white font-bold py-2 px-4 rounded-full flex items-center gap-2"
            >
              <FaCog />
              Gestión Masiva
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white overflow-x-auto rounded-lg shadow-md">
        <table className="shadow-md rounded-2xl w-full table-auto mt-4">
          <thead>
            <tr className="font-bold text-center text-sm md:text-base">
              <th className="text-title-table px-4 py-3">Nombre</th>
              <th className="text-title-table px-4 py-3">Nacimiento</th>
              <th className="text-title-table px-4 py-3">Correo</th>
              <th className="text-title-table px-4 py-3">Teléfono</th>
              {type === "students" ? (
                <>
                  <th className="text-title-table px-4 py-3">Salón</th>
                  <th className="text-title-table px-4 py-3">Grado</th>
                </>
              ) : (
                <>
                  <th className="text-title-table px-4 py-3">Materias</th>
                  <th className="text-title-table px-4 py-3">
                    Salones a Cargo
                  </th>
                  <th className="text-title-table px-4 py-3">Director Salón</th>
                </>
              )}
              {!isRestricted && (
                <th className="text-title-table px-4 py-3">Acción</th>
              )}
            </tr>
          </thead>
          <tbody>
            {usuariosActuales.map((user) => (
              <tr key={user.user_id} className="hover:bg-gray-figma text-center text-sm md:text-base">
                <td className="text-title-table font-bold px-4 py-3 flex items-center">
                  {!imageErrors[user.user_id] ? (
                    <div className="relative w-8 h-8 md:w-10 md:h-10">
                      {/* Skeleton Loader */}
                      {imageLoading[user.user_id] && (
                        <div className="absolute inset-0 rounded-full bg-gray-200 animate-pulse" />
                      )}
                      {/* Imagen */}
                      <img
                        src={user.image || "/default-avatar.png"}
                        alt={`${user.first_name}`}
                        className={`rounded-full w-8 h-8 md:w-10 md:h-10 cursor-pointer object-cover transition-opacity duration-300 ${
                          imageLoading[user.user_id] ? "opacity-0" : "opacity-100"
                        }`}
                        loading="lazy"
                        onLoad={() => handleImageLoad(user.user_id)}
                        onError={() => handleImageError(user.user_id)}
                      />
                    </div>
                  ) : (
                    <div className="bg-blue-400 text-white rounded-full flex items-center justify-center w-8 h-8 md:w-10 md:h-10 text-sm">
                      {user.first_name?.charAt(0).toUpperCase() || "U"}
                    </div>
                  )}

                  <span className="truncate ml-4">{`${user.first_name} ${user.second_name ? user.second_name + " " : ""} ${user.first_surname} ${user.second_surname ? user.second_surname : ""}`}</span>
                </td>
                <td className="text-gray font-medium px-4 py-3">
                  {user.fecha_nacimiento}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleShowModal("Correo", user.correo)}
                    className="text-main-bg-sidebar hover:text-gray"
                  >
                    <FaEnvelope size={22} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleShowModal("Teléfono", user.celular)}
                    className="text-main-blue hover:text-gray"
                  >
                    <FaPhone size={22} />
                  </button>
                </td>
                {type === "students" ? (
                  <>
                    <td className="text-title-table font-medium px-4 py-2 truncate">
                      {user.salon_nombre || "No Asignado"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-6 py-3 rounded-full truncate text-white ${
                          user.salon_grado === "No activo"
                            ? "bg-orange"
                            : user.salon_grado === "Ninguno"
                            ? "bg-main-blue"
                            : user.salon_grado === null
                            ? "bg-orange"
                            : "bg-green"
                        }`}
                      >
                        {user.salon_grado || "No Asignado"}
                      </span>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="text-title-table font-medium px-4 py-2 truncate">
                      <ul className="list-none space-y-1">
                        {user.asignaciones && user.asignaciones.length > 0 ? (
                          user.asignaciones.map((asignacion, index) => (
                            <li key={index} className="truncate">
                              {asignacion.asignatura_nombre}
                            </li>
                          ))
                        ) : (
                          <li>Ninguna</li>
                        )}
                      </ul>
                    </td>
                    <td className="text-title-table font-medium px-4 py-2 truncate">
                      <ul className="list-none space-y-1">
                        {user.asignaciones && user.asignaciones.length > 0 ? (
                          user.asignaciones.map((asignacion, index) => (
                            <li key={index} className="truncate">
                              {asignacion.salon_nombre}
                            </li>
                          ))
                        ) : (
                          <li>Ninguno</li>
                        )}
                      </ul>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-6 py-3 rounded-full truncate text-white ${
                          user.salon_director === "No activo"
                            ? "bg-orange"
                            : user.salon_director === "Ninguno"
                            ? "bg-main-blue"
                            : user.salon_director === null
                            ? "bg-orange"
                            : "bg-green"
                        }`}
                      >
                        {user.salon_director || "No Asignado"}
                      </span>
                    </td>
                  </>
                )}
                {!isRestricted && (
                  <td className="relative px-4 py-3 overflow-visible">
                    <button
                      onClick={(e) => toggleActionMenu(user.user_id, e)}
                      className="text-gray hover:text-black"
                    >
                      <FaEllipsisH />
                    </button>
                    {actionMenu === user.user_id && (
                      <div
                        ref={actionMenuRef}
                        className="fixed w-32 bg-white rounded-md shadow-lg z-50"
                        style={{
                          top: actionMenuPosition.top,
                          left: actionMenuPosition.left,
                        }}
                      >
                        <ul>
                          <li
                            className="px-4 py-2 text-main-bg-sidebar hover:bg-main-bg-sidebar hover:text-white cursor-pointer"
                            onClick={() => {
                              if (type === "teachers") {
                                navigate(`/profesores/editar/${user.user_id}`);
                              } else if (type === "students") {
                                navigate(`/estudiantes/editar/${user.user_id}`);
                              }
                              setActionMenu(null);
                            }}
                          >
                            Actualizar
                          </li>
                          <li
                            className="px-4 py-2 text-red-600 hover:text-white hover:bg-red-700 cursor-pointer"
                            onClick={() => {
                              openDeleteModal(user.user_id);
                              setActionMenu(null);
                            }}
                          >
                            Eliminar
                          </li>
                        </ul>
                      </div>
                    )}
                  </td>
                )}
              </tr>
              
            ))}
            
          </tbody>
          
        </table>
        
      </div>
      
            
      {/* Paginación */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPaginas}
        onPageChange={setCurrentPage}
      />

      {/* Modal para confirmar eliminación */}
      <ConfirmModal
        isOpen={isModalOpen}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      />

      {/* Modal para mostrar detalles */}
      {modalData.isOpen && (
        <Modal
          isOpen={modalData.isOpen}
          onClose={handleCloseModal}
          title={modalData.title}
          content={modalData.content}
        />
      )}

      <GestionMasivaModal
        isOpen={gestionMasivaOpen}
        onClose={() => setGestionMasivaOpen(false)}
        tipo={type}
        onResultadoMasivo={(resultado) => {
          setResultadoProgresivo(resultado);
        }}
      />

      {/* 🔹 NUEVO: Notificación progresiva fuera de todos los modales */}
      {resultadoProgresivo && (
        <ProgressiveNotification
          resultado={resultadoProgresivo}
          onClose={() => {
            setResultadoProgresivo(null);
          }}
        />
      )}
    </div>
  );
};

export default UserList;
